import { Injectable, inject } from '@angular/core';
import { AppConfig } from '../app-config';
import { APP_CONFIG } from '../app-config.token';
import { GenerateAnnexPreviewRequest } from '../contracts/requests/annexes/generate-annex-preview-request';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { SignAnnexRequest } from '../contracts/requests/sign-annex-request';
import { CreateAnnexRequest } from '../contracts/requests/annexes/create-annex-request';
import { Moment } from 'moment';
import { ConflictedAnnexesDto } from '../models/dtos/annexes/conflicted-annexes-dto';
import { AnnexListDto } from '../models/dtos/annexes/annex-list-dto';
import { AnnexDetailsDto } from '../models/dtos/annexes/annex-details-dto';
import { AnnexListWithDetailsResponse } from '../models/dtos/annexes/annex-list-with-details-dto';
import { DictionaryItem } from '../models/dictionary-item';
import { PendingAnnexDto } from '../models/dtos/annexes/pending-annex-dto';
import { Filter } from '../models/common/filter';
import { IPagedResult } from '../shared/models/PagedResult';
import { AnnexRegistryGridDto } from '../models/dtos/annexes/annex-registry-grid-dto';
import { BYPASS_ERROR_HANDLER } from '../common/utils/http-context-tokens';

@Injectable({
  providedIn: 'root',
})
export class AnnexService {
  private http = inject(HttpClient);
  private config = inject<AppConfig>(APP_CONFIG);

  private readonly annexesUrl = this.config.resourceApiURI + '/annexes';

  generateAnnexPreview(workerAgreementId: number, request: GenerateAnnexPreviewRequest) {
    const url = `${this.annexesUrl}/${workerAgreementId}/preview`;
    return this.http.post<Blob>(url, request, { responseType: 'blob' as 'json', observe: 'response' });
  }

  getAnnexComponentConfiguration(workerAgreementId: number): Observable<DictionaryItem[]> {
    const url = `${this.annexesUrl}/${workerAgreementId}/annexComponentConfiguration`;
    return this.http.get<DictionaryItem[]>(url);
  }

  createAnnex(workerAgreementId: number, request: CreateAnnexRequest): Observable<number> {
    const url = `${this.annexesUrl}/${workerAgreementId}`;
    const context = new HttpContext().set(BYPASS_ERROR_HANDLER, true);
    return this.http.put<number>(url, request, { context });
  }

  getAnnexForSignature(annexId: number): Observable<any> {
    const url = `${this.annexesUrl}/${annexId}/signatureData`;
    return this.http.get<any>(url);
  }

  submitSignature(annexId: number, request: SignAnnexRequest): Observable<any> {
    const url = `${this.annexesUrl}/${annexId}/sign`;
    return this.http.post<any>(url, JSON.stringify(request.SignatureFileBase64), { headers: { 'Content-Type': 'application/json' } });
  }

  generateAnnexApprovalCode(data: { AnnexId: number }): Observable<any> {
    const url = `${this.annexesUrl}/${data.AnnexId}/generateApprovalCode`;
    return this.http.post(url, {});
  }

  verifyAnnexApprovalCode(data: { AnnexId: number; VerificationCode: string; SignatureMethodId: number }): Observable<any> {
    const url = `${this.annexesUrl}/${data.AnnexId}/verifyApprovalCode`;
    return this.http.post(url, { VerificationCode: data.VerificationCode, SignatureMethodId: data.SignatureMethodId });
  }

  getConflictedAnnexes(
    workerAgreementId: number,
    changesWage: boolean,
    changesJobTitle: boolean,
    changesTemporaryWorkplace: boolean,
    changesSupervisor: boolean,
    changesWorkingTimeGroup: boolean,
    startDate: Moment,
    endDate: Moment | null,
  ): Observable<ConflictedAnnexesDto[]> {
    let params = new HttpParams()
      .set('workerAgreementId', `${workerAgreementId}`)
      .set('changesWage', `${changesWage}`)
      .set('changesJobTitle', `${changesJobTitle}`)
      .set('changesTemporaryWorkplace', `${changesTemporaryWorkplace}`)
      .set('changesSupervisor', `${changesSupervisor}`)
      .set('changesWorkingTimeGroup', `${changesWorkingTimeGroup}`)
      .set('startDate', `${startDate.format('YYYY-MM-DD')}`);

    if (endDate) {
      params = params.set('endDate', `${endDate.format('YYYY-MM-DD')}`);
    }

    const url = `${this.annexesUrl}/${workerAgreementId}/conflicted`;
    return this.http.get<ConflictedAnnexesDto[]>(url, { params: params });
  }

  getAnnexesList(workerAgreementId: number, showAll: boolean): Observable<AnnexListDto[]> {
    let params = new HttpParams()
      .set('showAll', `${showAll}`);

    const url = `${this.annexesUrl}/${workerAgreementId}/list`;
    return this.http.get<AnnexListDto[]>(url, { params: params });
  }

  showAnnexDocument(annexId: number) {
    const url = `${this.annexesUrl}/${annexId}/show`;
    return this.http.post<Blob>(url, null, { responseType: 'blob' as 'json', observe: 'response' });
  }

  getAnnexDetails(annexId: number): Observable<AnnexDetailsDto> {
    const url = `${this.annexesUrl}/${annexId}`;
    return this.http.get<AnnexDetailsDto>(url);
  }

  getAnnexListWithDetails(workerAgreementId: number): Observable<AnnexListWithDetailsResponse> {
    const url = `${this.annexesUrl}/${workerAgreementId}/listWithDetails`;
    return this.http.get<AnnexListWithDetailsResponse>(url);
  }

  getPendingToSign(): Observable<PendingAnnexDto[]> {
    const url = `${this.annexesUrl}/pendingToSign`;
    return this.http.get<PendingAnnexDto[]>(url);
  }

  canCreateAnnex(workerAgreementId: number): Observable<boolean> {
    const url = `${this.annexesUrl}/${workerAgreementId}/canCreate`;
    return this.http.get<boolean>(url);
  }

  getPagedAnnexRegistry(
    page: number,
    count: number,
    sortingField: string,
    sortingDirection: string = 'desc',
    filters: Filter[],
  ): Observable<IPagedResult<AnnexRegistryGridDto>> {
    const url = `${this.annexesUrl}/registry`;
    const params = new HttpParams()
      .set('page', `${page || 1}`)
      .set('count', `${count || 10}`)
      .set('sortingField', sortingField)
      .set('sortingDirection', sortingDirection)
      .set('filterString', JSON.stringify(filters));
    return this.http.get<IPagedResult<AnnexRegistryGridDto>>(url, { params });
  }
  reEnqueueImportTask(annexId: number): Observable<void> {
    return this.http.post<void>(`${this.annexesUrl}/${annexId}/importTasks/reEnqueue`, null);
  }

  skipImportTask(annexId: number): Observable<void> {
    return this.http.post<void>(`${this.annexesUrl}/${annexId}/importTasks/skip`, null);
  }

  markAllImportTasksAsManuallyCorrected(annexId: number): Observable<void> {
    return this.http.post<void>(`${this.annexesUrl}/${annexId}/importTasks/markAllAsManuallyCorrected`, null);
  }
}
